"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Blocks,
  BookOpen,
  CheckCircle2,
  ChefHat,
  CircleHelp,
  ExternalLink,
  HeartPulse,
  KeyRound,
  Lightbulb,
  ListTree,
  LogIn,
  Printer,
  Search,
  Settings2,
  Tags,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";

import { cn } from "@/lib/utils";
import {
  AUDIENCE_LABELS,
  RECOMMENDED_FLOW,
  TUTORIAL_CHAPTERS,
  TUTORIAL_ROUTES,
  TUTORIAL_UPDATED_AT,
  type TutorialAudience,
  type TutorialChapter,
  type TutorialIcon,
  type TutorialRoute,
  type TutorialTopic,
} from "@/lib/tutorial-content";

type AudienceFilter = "TODOS" | TutorialAudience;

const ICONS: Record<TutorialIcon, LucideIcon> = {
  start: LogIn,
  membros: Users,
  categorias: Tags,
  apps: Blocks,
  motor: Settings2,
  receitas: ChefHat,
  saude: HeartPulse,
  help: CircleHelp,
};

const FILTERS: readonly { value: AudienceFilter; label: string }[] = [
  { value: "TODOS", label: "Todos os perfis" },
  { value: "SUPER_ADMIN", label: AUDIENCE_LABELS.SUPER_ADMIN },
  { value: "ADMIN", label: AUDIENCE_LABELS.ADMIN },
  { value: "DESENVOLVEDOR", label: AUDIENCE_LABELS.DESENVOLVEDOR },
  { value: "MEMBRO", label: AUDIENCE_LABELS.MEMBRO },
  { value: "OBSERVADOR", label: AUDIENCE_LABELS.OBSERVADOR },
];

function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR");
}

function topicText(chapter: TutorialChapter, topic: TutorialTopic): string {
  return normalize(
    [
      chapter.kicker,
      chapter.title,
      chapter.description,
      topic.title,
      topic.summary,
      topic.where,
      topic.result,
      topic.warning,
      ...(topic.keywords ?? []),
      ...(topic.tips ?? []),
      ...topic.steps.flatMap((step) => [step.title, step.description]),
    ]
      .filter(Boolean)
      .join(" "),
  );
}

function routeText(route: TutorialRoute): string {
  return normalize([route.path, route.page, route.purpose].filter(Boolean).join(" "));
}

function matchesAudience(
  audiences: readonly TutorialAudience[],
  selected: AudienceFilter,
): boolean {
  return selected === "TODOS" || audiences.includes(selected);
}

/** Mesma pílula de código do tutorial original (page.tsx antigo) — reaproveitada aqui. */
function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded bg-slate-700/60 px-1.5 py-0.5 font-mono text-[12px] text-slate-200">
      {children}
    </span>
  );
}

function AudienceBadges({ audiences }: { audiences: readonly TutorialAudience[] }) {
  if (audiences.length === Object.keys(AUDIENCE_LABELS).length) {
    return (
      <span className="rounded-full bg-slate-700/50 px-2 py-0.5 text-xs text-slate-300">
        Todos os perfis
      </span>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {audiences.map((audience) => (
        <span
          key={audience}
          className="rounded-full border border-orange-500/20 bg-orange-500/10 px-2 py-0.5 text-xs text-orange-300"
        >
          {AUDIENCE_LABELS[audience]}
        </span>
      ))}
    </div>
  );
}

function TopicCard({
  topic,
  chapterIcon: ChapterIcon,
}: {
  topic: TutorialTopic;
  chapterIcon: LucideIcon;
}) {
  return (
    <article
      id={topic.id}
      className="scroll-mt-6 rounded-2xl border border-slate-700/50 bg-slate-800/50 p-4 sm:p-6 print:break-inside-auto print:border-slate-300"
    >
      <header className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <span className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-slate-700/50 text-slate-200">
              <ChapterIcon className="size-4" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h3 className="text-xl font-semibold tracking-tight text-slate-100">
                {topic.title}
              </h3>
              {topic.where && (
                <p className="mt-1 break-words text-xs text-slate-500">
                  <span className="font-medium text-slate-300">Onde encontrar:</span>{" "}
                  {topic.where}
                </p>
              )}
            </div>
          </div>
          <AudienceBadges audiences={topic.audiences} />
        </div>
        <p className="max-w-3xl text-sm leading-6 text-slate-400">{topic.summary}</p>
      </header>

      <div className="mt-5 border-t border-slate-700/50 pt-5">
        <h4 className="text-sm font-semibold tracking-tight text-slate-200">Como usar</h4>
        <ol className="mt-3 space-y-3">
          {topic.steps.map((step, index) => (
            <li key={`${topic.id}-${step.title}`} className="flex gap-3">
              <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-orange-500/20 text-xs font-bold text-orange-400">
                {index + 1}
              </span>
              <div className="min-w-0 pt-0.5 text-sm leading-6">
                <span className="font-medium text-slate-100">{step.title}.</span>{" "}
                <span className="text-slate-400">{step.description}</span>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {topic.result && (
        <div className="mt-5 flex gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3 text-sm leading-6">
          <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-400" aria-hidden="true" />
          <p>
            <span className="font-medium text-slate-100">Resultado esperado:</span>{" "}
            <span className="text-slate-400">{topic.result}</span>
          </p>
        </div>
      )}

      {topic.warning && (
        <div className="mt-3 flex gap-3 rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 text-sm leading-6">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-400" aria-hidden="true" />
          <p>
            <span className="font-medium text-slate-100">Atenção:</span>{" "}
            <span className="text-slate-400">{topic.warning}</span>
          </p>
        </div>
      )}

      {topic.tips && topic.tips.length > 0 && (
        <div className="mt-3 flex gap-3 rounded-xl border border-blue-500/20 bg-blue-500/10 px-4 py-3 text-sm leading-6 text-blue-200">
          <Lightbulb className="mt-0.5 size-4 shrink-0 text-blue-300" aria-hidden="true" />
          <div>
            <p className="font-medium">Dicas</p>
            <ul className="mt-1 list-disc space-y-1 pl-4">
              {topic.tips.map((tip) => (
                <li key={tip}>{tip}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {topic.href && (
        <div className="mt-5 print:hidden">
          <Link
            href={topic.href}
            prefetch={false}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-200 transition hover:border-orange-500/50 hover:text-orange-300"
          >
            Abrir esta área
            <ArrowRight className="size-3.5" aria-hidden="true" />
          </Link>
        </div>
      )}
    </article>
  );
}

function RouteDirectory({ routes }: { routes: readonly TutorialRoute[] }) {
  if (routes.length === 0) return null;

  return (
    <section id="diretorio-de-paginas" className="scroll-mt-6 space-y-4">
      <header>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Atlas do aplicativo
        </p>
        <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-100">
          Diretório de páginas
        </h2>
        <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-400">
          As rotas com <Kbd>[id]</Kbd> são abertas a partir de um item real, como um membro,
          uma categoria ou um app.
        </p>
      </header>

      <div className="space-y-3 md:hidden">
        {routes.map((route) => (
          <div
            key={`${route.path}-${route.page}`}
            className="rounded-2xl border border-slate-700/50 bg-slate-800/30 p-4"
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <span className="font-medium text-slate-100">{route.page}</span>
              {route.redirect && (
                <span className="rounded-full bg-slate-700/50 px-2 py-0.5 text-xs text-slate-300">
                  Redireciona
                </span>
              )}
            </div>
            <p className="mt-1">
              <Kbd>{route.path}</Kbd>
            </p>
            <p className="mt-3 text-sm text-slate-400">{route.purpose}</p>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <AudienceBadges audiences={route.audiences} />
              {route.href && (
                <Link
                  href={route.href}
                  prefetch={false}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-slate-300 hover:text-orange-300"
                >
                  Abrir
                  <ExternalLink className="size-3.5" aria-hidden="true" />
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="hidden overflow-hidden rounded-2xl border border-slate-700/50 md:block">
        <table className="w-full border-collapse text-left text-sm">
          <thead className="bg-slate-800/60 text-xs text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Página e endereço</th>
              <th className="px-4 py-3 font-medium">Para que serve</th>
              <th className="px-4 py-3 font-medium">Perfis</th>
              <th className="w-20 px-4 py-3 text-right font-medium print:hidden">Acesso</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {routes.map((route) => (
              <tr key={`${route.path}-${route.page}`} className="align-top">
                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2 font-medium text-slate-100">
                    {route.page}
                    {route.redirect && (
                      <span className="rounded-full bg-slate-700/50 px-2 py-0.5 text-xs text-slate-300">
                        Redireciona
                      </span>
                    )}
                  </div>
                  <p className="mt-1">
                    <Kbd>{route.path}</Kbd>
                  </p>
                </td>
                <td className="px-4 py-3 leading-6 text-slate-400">{route.purpose}</td>
                <td className="px-4 py-3">
                  <AudienceBadges audiences={route.audiences} />
                </td>
                <td className="px-4 py-3 text-right print:hidden">
                  {route.href ? (
                    <Link
                      href={route.href}
                      prefetch={false}
                      className="text-sm text-slate-300 hover:text-orange-300"
                      aria-label={`Abrir ${route.page}`}
                    >
                      Abrir
                    </Link>
                  ) : (
                    <span className="text-slate-600">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function TutorialGuide() {
  const [query, setQuery] = useState("");
  const [audience, setAudience] = useState<AudienceFilter>("TODOS");
  const normalizedQuery = normalize(query.trim());

  const visibleChapters = useMemo(
    () =>
      TUTORIAL_CHAPTERS.map((chapter) => ({
        ...chapter,
        topics: chapter.topics.filter(
          (topic) =>
            matchesAudience(topic.audiences, audience) &&
            (!normalizedQuery || topicText(chapter, topic).includes(normalizedQuery)),
        ),
      })).filter((chapter) => chapter.topics.length > 0),
    [audience, normalizedQuery],
  );

  const visibleRoutes = useMemo(
    () =>
      TUTORIAL_ROUTES.filter(
        (route) =>
          matchesAudience(route.audiences, audience) &&
          (!normalizedQuery || routeText(route).includes(normalizedQuery)),
      ),
    [audience, normalizedQuery],
  );

  const visibleTopicCount = visibleChapters.reduce(
    (total, chapter) => total + chapter.topics.length,
    0,
  );
  const hasResults = visibleTopicCount > 0 || visibleRoutes.length > 0;

  return (
    <div className="space-y-8">
      <section className="relative overflow-hidden rounded-2xl border border-slate-700/50 bg-gradient-to-br from-slate-900 via-slate-900 to-orange-950/30 px-5 py-8 sm:px-8 sm:py-10 print:border-0 print:bg-transparent print:p-0">
        <KeyRound
          aria-hidden="true"
          className="pointer-events-none absolute -right-8 -top-8 size-56 select-none text-orange-500 opacity-[0.08] sm:size-64 print:hidden"
        />
        <div className="relative max-w-3xl space-y-5">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-orange-300/80">
            <BookOpen className="size-4" aria-hidden="true" />
            Manual oficial de uso
          </div>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-100 sm:text-4xl">
              Aprenda a Maranata Key do zero
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-400 sm:text-base">
              A central de identidade e permissões da Suite Maranata: quem entra em cada
              aplicativo e o que pode fazer dentro dele — mesmo que você nunca tenha aberto o
              painel.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 print:hidden">
            <a
              href="#manual-completo"
              className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600"
            >
              Começar o tutorial
              <ArrowRight className="size-4" aria-hidden="true" />
            </a>
            <Link
              href="/"
              prefetch={false}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-600/60 px-4 py-2.5 text-sm font-medium text-slate-200 transition hover:bg-slate-800"
            >
              Abrir o portal
            </Link>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-300 transition hover:bg-slate-800/60 hover:text-slate-100"
            >
              <Printer className="size-4" aria-hidden="true" />
              Imprimir / Salvar PDF
            </button>
          </div>
          <p className="text-xs text-slate-500">
            Revisado com base no aplicativo em {TUTORIAL_UPDATED_AT}.
          </p>
        </div>
      </section>

      <div id="fluxo-visual" className="scroll-mt-6 rounded-2xl border border-slate-700/50 bg-slate-800/30 p-5">
        <h2 className="text-xl font-semibold tracking-tight text-slate-100">
          A Key completa em cinco movimentos
        </h2>
        <p className="mt-1 text-sm text-slate-400">
          Esta é a ordem mais segura. Os capítulos abaixo explicam cada etapa em detalhes.
        </p>
        <ol className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {RECOMMENDED_FLOW.map((item, index) => (
            <li
              key={item.title}
              className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-3"
            >
              <div className="flex items-center gap-2">
                <span className="inline-flex size-6 items-center justify-center rounded-full bg-orange-500/20 text-xs font-bold text-orange-400">
                  {index + 1}
                </span>
                <span className="text-sm font-medium text-slate-100">{item.title}</span>
              </div>
              <p className="mt-2 text-xs leading-5 text-slate-400">{item.description}</p>
            </li>
          ))}
        </ol>
      </div>

      <section
        id="manual-completo"
        className="scroll-mt-6 space-y-4 rounded-2xl border border-slate-700/50 bg-slate-800/30 p-4 sm:p-5 print:hidden"
      >
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Encontre uma resposta
            </p>
            <h2 className="mt-1 text-xl font-semibold tracking-tight text-slate-100">
              Buscar e filtrar o manual
            </h2>
          </div>
          <p className="text-xs text-slate-500" aria-live="polite">
            {visibleTopicCount} {visibleTopicCount === 1 ? "tópico" : "tópicos"}
            {visibleRoutes.length > 0 &&
              ` · ${visibleRoutes.length} ${visibleRoutes.length === 1 ? "página" : "páginas"}`}
          </p>
        </div>

        <div className="space-y-2">
          <label htmlFor="tutorial-search" className="text-sm font-medium text-slate-300">
            Buscar no tutorial
          </label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-500"
              aria-hidden="true"
            />
            <input
              id="tutorial-search"
              name="tutorial-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Ex.: conceder acesso, categoria, recurso, observador…"
              className="min-h-11 w-full rounded-xl border border-slate-700 bg-slate-900 py-2 pl-9 pr-10 text-sm text-slate-100 placeholder:text-slate-600 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/30"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-1 top-1/2 inline-flex size-9 -translate-y-1/2 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-800 hover:text-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40"
                aria-label="Limpar busca"
              >
                <X className="size-4" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-slate-300">Mostrar para</legend>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => setAudience(filter.value)}
                aria-pressed={audience === filter.value}
                className={cn(
                  "min-h-10 rounded-xl px-3 py-2 text-sm font-medium transition",
                  audience === filter.value
                    ? "bg-orange-500 text-slate-950"
                    : "border border-slate-700 bg-slate-900 text-slate-300 hover:border-orange-500/40 hover:text-orange-300",
                )}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </fieldset>
      </section>

      {hasResults ? (
        <div className="grid items-start gap-8 lg:grid-cols-[15rem_minmax(0,1fr)]">
          <aside className="print:hidden lg:sticky lg:top-6">
            <nav
              aria-label="Índice do tutorial"
              className="rounded-2xl border border-slate-700/50 bg-slate-800/50 p-3"
            >
              <div className="flex items-center gap-2 px-2 py-2 text-sm font-medium text-slate-200">
                <ListTree className="size-4" aria-hidden="true" />
                Índice
              </div>
              <ol className="mt-1 space-y-1">
                {visibleChapters.map((chapter) => (
                  <li key={chapter.id}>
                    <a
                      href={`#${chapter.id}`}
                      className="block rounded-md px-2 py-2 text-sm leading-5 text-slate-400 transition-colors hover:bg-slate-700/40 hover:text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40"
                    >
                      {chapter.title}
                    </a>
                  </li>
                ))}
                {visibleRoutes.length > 0 && (
                  <li>
                    <a
                      href="#diretorio-de-paginas"
                      className="block rounded-md px-2 py-2 text-sm leading-5 text-slate-400 transition-colors hover:bg-slate-700/40 hover:text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40"
                    >
                      Diretório de páginas
                    </a>
                  </li>
                )}
              </ol>
            </nav>
          </aside>

          <div className="min-w-0 space-y-12">
            <nav
              aria-label="Índice móvel do tutorial"
              className="flex gap-2 overflow-x-auto pb-2 print:hidden lg:hidden"
            >
              {visibleChapters.map((chapter) => (
                <a
                  key={chapter.id}
                  href={`#${chapter.id}`}
                  className="shrink-0 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm font-medium text-slate-200"
                >
                  {chapter.title}
                </a>
              ))}
              {visibleRoutes.length > 0 && (
                <a
                  href="#diretorio-de-paginas"
                  className="shrink-0 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm font-medium text-slate-200"
                >
                  Páginas
                </a>
              )}
            </nav>

            {visibleChapters.map((chapter) => {
              const ChapterIcon = ICONS[chapter.icon];
              return (
                <section key={chapter.id} id={chapter.id} className="scroll-mt-6 space-y-4">
                  <header className="flex items-start gap-3">
                    <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg bg-orange-500/20 text-orange-400">
                      <ChapterIcon className="size-5" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        {chapter.kicker}
                      </p>
                      <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-100">
                        {chapter.title}
                      </h2>
                      <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-400">
                        {chapter.description}
                      </p>
                    </div>
                  </header>

                  <div className="space-y-4">
                    {chapter.topics.map((topic) => (
                      <TopicCard key={topic.id} topic={topic} chapterIcon={ChapterIcon} />
                    ))}
                  </div>
                </section>
              );
            })}

            <RouteDirectory routes={visibleRoutes} />
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center rounded-2xl border border-slate-700/50 bg-slate-800/30 py-12 text-center">
          <Search className="size-8 text-slate-600" aria-hidden="true" />
          <h2 className="mt-4 text-lg font-semibold tracking-tight text-slate-100">
            Nenhum resultado encontrado
          </h2>
          <p className="mt-1 max-w-md text-sm text-slate-400">
            Tente outra palavra ou volte para Todos os perfis.
          </p>
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setAudience("TODOS");
            }}
            className="mt-4 rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm text-slate-200 hover:border-orange-500/40 hover:text-orange-300"
          >
            Limpar filtros
          </button>
        </div>
      )}

      <p className="text-center text-xs text-slate-600 print:hidden">
        Maranata Key — central de identidade e permissões da Suite · IME Maranata
      </p>
    </div>
  );
}
